import { ImpactDataset } from "@/hooks/useEcoInventDatasets";
import { IImpact } from "@/pages/dashboard/utils/types";
import axios from "axios";
import { Result } from "./types";

export type ContractType = "actual" | "estimate";

export type UserType = "Company" | "Supplier";

export type UtilityStatus = "looks_good" | "incomplete" | "out_of_scope";

export type FacilityType =
  | "Owned"
  | "Unknown"
  | "Upstream - Purchased goods and services"
  | "Upstream - Transportation and distribution"
  | "Upstream - Leased asset"
  | "Upstream - Unknown"
  | "Downstream - Transportation and distribution"
  | "Downstream - Processing of sold product"
  | "Downstream - Leased asset"
  | "Downstream - Franchise"
  | "Downstream - Unknown";

export const facilityTypeLabels = [
  "Owned",
  "Unknown",
  "Upstream - Purchased goods and services",
  "Upstream - Transportation and distribution",
  "Upstream - Leased asset",
  "Upstream - Unknown",
  "Downstream - Transportation and distribution",
  "Downstream - Processing of sold product",
  "Downstream - Leased asset",
  "Downstream - Franchise",
  "Downstream - Unknown",
];

export const facilityTypeTree = [
  { title: "Owned or rented facility", value: "Owned or rented facility" },
  { title: "Supplier production site", value: "Supplier production site" },
  {
    title: "Distribution center (third party - upstream)",
    value: "Distribution center (third party - upstream)",
  },
  {
    title: "Distribution center (third party - downstream)",
    value: "Distribution center (third party - downstream)",
  },
  {
    title: "Franchise store",
    value: "Franchise store",
  },
  { title: "Unknown", value: "Unknown" },
];

export interface IFacility {
  category: string;
  electricity_used: boolean;
  heat_used: boolean;
  water_used: boolean;
  empty: number;
  facility_name: string;
  address: string;
  id: number;
  status: UtilityStatus;
  geography: string | null;
  deletable: boolean;
  address_is_valid: boolean;
  type: FacilityType;
  electricity_not_used: boolean;
  heat_not_used: boolean;
  water_not_used: boolean;
  cooling_not_used: boolean;
  residual_waste_not_used: boolean;
  goods_and_services_not_used: boolean;
  consumable_not_used: boolean;
  packaging_not_used: boolean;
  electricity_status: "complete" | "incomplete";
  heat_status: "complete" | "incomplete";
  water_status: "complete" | "incomplete";
  cooling_status: "complete" | "incomplete";
  residual_waste_status: "complete" | "incomplete";
  goods_and_services_status: "complete" | "incomplete";
  consumable_status: "complete" | "incomplete";
  packaging_status: "complete" | "incomplete";
  has_relation: boolean;
  published_id?: string;
}

export interface CreateFacility {
  facility_name: string;
  address: string;
  category: string | null;
  type: FacilityType;
}

export interface UtilitySourceBase {
  label: string;
  amount: number;
  unit: string;
}

export interface ResidualWasteSource extends UtilitySourceBase {
  activity_name: string;
}

export interface GoodsAndServicesSource extends UtilitySourceBase {
  name: string;
  id: number;
}

export interface ConsumableSource extends UtilitySourceBase {
  id: number;
  name: string;
  amount: number;
  unit: string;
  notes: string;
}

export interface PackagingSource extends UtilitySourceBase {
  id: number;
  name: string;
  amount: number;
  unit: string;
  supplier_name?: string;
  supplier_address?: string;
}

export interface UtilitySource extends UtilitySourceBase {
  urn: string;
  details: string;
  percentage: number;
}

export interface ElectricitySource extends UtilitySource {
  unit: "kWh";
  voltage: string;
  geography: string;
  capital_owned: boolean;
  owned: string;
  parent: string;
  type: string;
}

export interface WaterSource extends UtilitySource {
  unit: "l" | "m3";
}

export interface CoolingSource extends UtilitySource {}

export interface HeatSource extends UtilitySource {
  type_label: "NATURAL_GAS" | "OTHER" | null;
  generator_type_label: "DISTRICT" | "CENTRAL" | null;
  unit: "mj" | "m3";
}

export interface PutUtilitySource {
  source: string;
  amount: number;
  unit: "kwh" | "mj" | "l" | "m3";
}

export interface PutNewUtilitySource {
  urn: string;
  unit: string;
  amount: number;
  percentage: number;
  capital_owned: boolean;
}

export interface AbstractUtilityContract {
  id: number;
  facility_id: number;
  end_date: string;
  start_date: string;
  type: UtilityType;
  contract_type: ContractType;
  sources: UtilitySourceBase[];
  last_updated_by: UserType;
}

export interface ConsumableContract extends AbstractUtilityContract {
  type: "consumable";
  sources: ConsumableSource[];
}

export interface PackagingContract extends AbstractUtilityContract {
  type: "packaging";
  sources: PackagingSource[];
}

export interface WaterContract extends AbstractUtilityContract {
  type: "water";
  sources: WaterSource[];
}

export interface CoolingContract extends AbstractUtilityContract {
  type: "cooling";
  sources: CoolingSource[];
}

export interface HeatContract extends AbstractUtilityContract {
  type: "heat";
  sources: HeatSource[];
}

export interface ElectricityContract extends AbstractUtilityContract {
  type: "electricity";
  sources: ElectricitySource[];
}

export interface ResidualWasteContract extends AbstractUtilityContract {
  type: "residual_waste";
  sources: ResidualWasteSource[];
}

export interface GoodsAndServicesContract extends AbstractUtilityContract {
  type: "goods_and_services";
  sources: GoodsAndServicesSource[];
}

export interface PutUtilityContract {
  id?: number;
  facility_id: number;
  end_date: string;
  start_date: string;
  type: UtilityType;
  sources: PutNewUtilitySource[];
  contract_type: ContractType;
}

export interface UpdateFacility {
  address: string;
  facility_name: string;
  category: string | null;
  type: FacilityType;
}

export const updateFacility = async ({
  facilityId,
  facility: utility,
}: {
  facilityId: number;
  facility: UpdateFacility;
}) => {
  return axios.post<Result>(`/facilities/${facilityId}`, utility);
};

export interface SetFacilityUsed {
  used: boolean;
  year: number;
  utility_type?: UtilityType;
}

export const setUsed = async ({
  facilityId,
  setUsed,
}: {
  facilityId: number;
  setUsed: SetFacilityUsed;
}) => {
  return axios.post<Result>(`/facilities/${facilityId}/used`, setUsed);
};

export const insertFacility = async ({ facility }: { facility: CreateFacility }) => {
  return axios.post<{ id: string }>(`/facilities`, facility);
};

export const updateContract = async ({
  contractId,
  contract,
}: {
  contractId: number;
  contract: PutUtilityContract;
}) => {
  return axios.post<Result>(`/contracts/${contractId}`, contract);
};

export const insertContract = async ({ contract }: { contract: PutUtilityContract }) => {
  return axios.post<Result>(`/contracts`, contract);
};

export const getFacility = async (facilityId: number) => {
  let url = `/facilities/${facilityId}`;
  const { data } = await axios.get<IFacility>(url);
  return data;
};

export const utilityTypes = [
  "electricity",
  "heat",
  "water",
  "residual_waste",
  "cooling",
  "goods_and_services",
  "consumable",
  "packaging",
] as const;

export type UtilityType = (typeof utilityTypes)[number];

export type IUtilityContract =
  | WaterContract
  | HeatContract
  | ResidualWasteContract
  | ElectricityContract
  | GoodsAndServicesContract
  | CoolingContract
  | ConsumableContract
  | PackagingContract;

export interface IUtilityContractGroup {
  used: boolean;
  status: "complete" | "incomplete";
  contracts: IUtilityContract[];
}
export interface GetFacilityContracts {
  contracts: Record<number, Record<UtilityType, IUtilityContractGroup>>;
  total_contracts: number;
}

export const getFacilityContracts = async (facilityId: number) => {
  let url = `/facilities/${facilityId}/contracts`;
  const { data } = await axios.get<GetFacilityContracts>(url);
  return data;
};

export const getUtilityContract = async (contractId: number) => {
  let url = `/contracts/${contractId}`;
  const { data } = await axios.get<Result<AbstractUtilityContract>>(url);
  return data;
};

export const deleteUtilityContract = async ({
  contractId,
}: {
  utilityId: number;
  contractId: number;
}) => {
  let url = `/contracts/${contractId}`;
  return axios.delete<Result<AbstractUtilityContract>>(url);
};

export interface ElectricityOption {
  parent: string;
  urn: string;
  label: string;
  source: string;
  type: string;
  voltage: string[];
}

export interface ElectricityDataset {
  urn: string;
  source_urn: string;
  label: string;
  type: string;
  capital_owned: boolean;
  source: string;
  impact: IImpact;
  geography: string;
  voltage: string;
  unit: string;
  owned: boolean;
}

export const getElectricityOptions = async ({ facilityId }: { facilityId: number }) => {
  let url = `/contracts/options/electricity?facility_id=${facilityId}`;
  const { data } = await axios.get<{
    datasets: ElectricityDataset[];
    green: ElectricityOption[];
    grey: ElectricityOption[];
    mix: ElectricityOption[];
  }>(url);
  return data;
};

export interface CoolingOption {
  label: string;
  unit: string;
  urn: string;
  geography: string;
  impact: IImpact;
}

export const getCoolingOptions = async ({ facilityId }: { facilityId: number }) => {
  let url = `/contracts/options/cooling?facility_id=${facilityId}`;
  const { data } = await axios.get<CoolingOption[]>(url);
  return data;
};

export interface WaterOption {
  label: string;
  unit: string;
  urn: string;
  geography: string;
  impact: IImpact;
}

export const getWaterOptions = async ({ facilityId }: { facilityId: number }) => {
  let url = `/contracts/options/water?facility_id=${facilityId}`;
  const { data } = await axios.get<WaterOption[]>(url);
  return data;
};

export interface HeatOption {
  id: string;
  category_label: string;
  source_label: string;
  scope_label: string;
  method_label: string;
  type_label: string;
  label: string;
  units: string[];
  parent: string;
}

export const getHeatOptions = async ({ facilityId }: { facilityId: number }) => {
  let url = `/contracts/options/heat?facility_id=${facilityId}`;
  const { data } = await axios.get<HeatOption[]>(url);
  return data;
};

export interface PutPostUtilityItem {
  facility_id: number;
  name: string;
  amount: string;
  start_date: string;
  end_date: string;
  impact_dataset_id: number;
  contract_type: "actual";
}

export const createResidualWaste = async ({ contract }: { contract: PutPostUtilityItem }) => {
  return axios.post<Result>(`/residual_waste`, contract);
};

export const updateResidualWaste = async ({
  contract,
  contractId,
}: {
  contractId: number;
  contract: PutPostUtilityItem;
}) => {
  return axios.put<Result>(`/residual_waste/${contractId}`, contract);
};

export interface UtilityItem {
  contract_id: number;
  name: string;
  amount: number;
  start_date: string;
  end_date: string;
  match?: ImpactDataset;
}

export const getWaste = async (contract_id: number) => {
  const { data } = await axios.get<UtilityItem>(`/residual_waste/${contract_id}`);
  return data;
};

export const createCapitalGoods = async ({ contract }: { contract: PutPostUtilityItem }) => {
  return axios.post<Result>(`/capital_goods`, contract);
};

export const updateCapitalGoods = async ({
  contract,
  contractId,
}: {
  contractId: number;
  contract: PutPostUtilityItem;
}) => {
  return axios.put<Result>(`/capital_goods/${contractId}`, contract);
};

export interface PutPostUtilityConsumable {
  facility_id: number;
  contract_type: "actual";
  start_date: string;
  end_date: string;
  id: number;
  amount: number;
  unit: string;
  notes: string;
}

export const createUtilityConsumable = async ({
  contract,
}: {
  contract: PutPostUtilityConsumable;
}) => {
  return axios.post<Result>(`/utility_consumables`, contract);
};

export const updateUtilityConsumable = async ({
  contract,
  contractId,
}: {
  contractId: number;
  contract: PutPostUtilityConsumable;
}) => {
  return axios.put<Result>(`/utility_consumables/${contractId}`, contract);
};

export const getCapitalGoods = async (contract_id: number) => {
  const { data } = await axios.get<UtilityItem>(`/capital_goods/${contract_id}`);
  return data;
};

export interface GetFacilityOverviewYears {
  year: number;
  status: "complete" | "incomplete";
}

export interface GetFacilityYears {
  year: number;
  status: "complete" | "incomplete";
  order_related: boolean;
}

export const getFacilityOverviewYears = async () => {
  const { data } = await axios.get<GetFacilityOverviewYears[]>("/facilities/years");
  return data;
};

export const getFacilityYears = async ({ facilityId }: { facilityId: number }) => {
  const { data } = await axios.get<GetFacilityYears[]>(`/facilities/${facilityId}/years`);
  return data;
};

export const getPublishedIdDetail = async () => {
  const { data } = await axios.get<{ company_name: string; facility_id: number }>(
    `/facilities/publish/detail`
  );
  return data;
};

export const getExportFacilitiesUrl = async () => {
  const { data } = await axios.get<{ url: string }>(`/facilities/export`);
  return data.url;
};

export const getExportUtilitiesUrl = async (year: number) => {
  const { data } = await axios.get<{ url: string }>(`/contracts/utilities/export`, {
    params: { year },
  });
  return data.url;
};

export const getExportGoodsAndServicesUrl = async (year: number) => {
  const { data } = await axios.get<{ url: string }>(`/contracts/goods_and_services/export`, {
    params: { year },
  });
  return data.url;
};

export const getExportLogisticsPackagingUrl = async (year: number) => {
  const { data } = await axios.get<{ url: string }>(`/contracts/logistics_packaging/export`, {
    params: { year },
  });
  return data.url;
};
